<template>
  <div class="alert-dashboard">
    <!-- Alert Statistics Cards -->
    <el-row :gutter="20" class="stats-row">
      <el-col :xs="12" :sm="6">
        <el-card class="stat-card critical">
          <div class="stat-content">
            <i class="el-icon-warning"></i>
            <div class="stat-info">
              <div class="stat-value">{{ statistics.critical || 0 }}</div>
              <div class="stat-label">{{ $t("criticalAlerts") }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="6">
        <el-card class="stat-card warning">
          <div class="stat-content">
            <i class="el-icon-warning-outline"></i>
            <div class="stat-info">
              <div class="stat-value">{{ statistics.warning || 0 }}</div>
              <div class="stat-label">{{ $t("warningAlerts") }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="6">
        <el-card class="stat-card info">
          <div class="stat-content">
            <i class="el-icon-info"></i>
            <div class="stat-info">
              <div class="stat-value">{{ statistics.info || 0 }}</div>
              <div class="stat-label">{{ $t("infoAlerts") }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="6">
        <el-card class="stat-card total">
          <div class="stat-content">
            <i class="el-icon-bell"></i>
            <div class="stat-info">
              <div class="stat-value">
                {{ statistics.totalUnresolved || 0 }}
              </div>
              <div class="stat-label">{{ $t("totalUnresolved") }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Alert Type Distribution -->
    <el-row :gutter="20" class="chart-row">
      <el-col :xs="24" :sm="12">
        <el-card>
          <div slot="header" class="card-header">
            <span>{{ $t("alertsByType") }}</span>
            <el-button
              size="mini"
              type="primary"
              @click="refreshAlerts"
              :loading="loading"
            >
              <i class="el-icon-refresh"></i> {{ $t("refresh") }}
            </el-button>
          </div>
          <div class="alert-type-list">
            <div
              v-for="(count, type) in statistics.byType"
              :key="type"
              class="alert-type-item"
            >
              <span class="type-name">{{ getAlertTypeName(type) }}</span>
              <el-tag size="small">{{ count }}</el-tag>
            </div>
            <div
              v-if="
                !statistics.byType ||
                  Object.keys(statistics.byType).length === 0
              "
              class="no-data"
            >
              {{ $t("noAlertsFound") }}
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :sm="12">
        <el-card>
          <div slot="header" class="card-header">
            <span>{{ $t("quickActions") }}</span>
          </div>
          <div class="quick-actions">
            <el-button
              type="primary"
              icon="el-icon-search"
              @click="viewAllAlerts"
              plain
            >
              {{ $t("viewAllAlerts") }}
            </el-button>
            <el-button
              type="success"
              icon="el-icon-refresh"
              @click="checkAlertsNow"
              :loading="checkingAlerts"
              plain
            >
              {{ $t("checkNow") }}
            </el-button>
            <el-button
              type="info"
              icon="el-icon-setting"
              @click="openSettings"
              plain
            >
              {{ $t("alertSettings") }}
            </el-button>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Recent Alerts Table -->
    <el-card class="recent-alerts-card">
      <div slot="header" class="card-header">
        <span>{{ $t("recentAlerts") }}</span>
        <el-tag v-if="statistics.unread > 0" type="danger" size="small">
          {{ statistics.unread }} {{ $t("unread") }}
        </el-tag>
      </div>

      <el-table
        :data="recentAlerts"
        v-loading="loading"
        style="width: 100%"
        @row-click="handleRowClick"
        :row-class-name="getRowClassName"
      >
        <el-table-column type="expand">
          <template slot-scope="props">
            <div class="alert-details">
              <p>
                <strong>{{ $t("message") }}:</strong> {{ props.row.message }}
              </p>
              <p v-if="props.row.entityName">
                <strong>{{ $t("entity") }}:</strong> {{ props.row.entityName }}
              </p>
              <p v-if="props.row.currentValue">
                <strong>{{ $t("currentValue") }}:</strong>
                {{ formatCurrency(props.row.currentValue) }}
              </p>
              <p v-if="props.row.thresholdValue">
                <strong>{{ $t("threshold") }}:</strong>
                {{ formatCurrency(props.row.thresholdValue) }}
              </p>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="severity" :label="$t('severity')" width="120">
          <template slot-scope="scope">
            <el-tag
              :type="getSeverityType(scope.row.severity)"
              size="small"
              effect="dark"
            >
              {{ getSeverityName(scope.row.severity) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="title" :label="$t('title')" min-width="200">
          <template slot-scope="scope">
            <div class="alert-title">
              <i
                v-if="!scope.row.isRead"
                class="el-icon-message-solid unread-icon"
              ></i>
              {{ scope.row.title }}
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="alertType" :label="$t('type')" width="180">
          <template slot-scope="scope">
            {{ getAlertTypeName(scope.row.alertType) }}
          </template>
        </el-table-column>

        <el-table-column prop="createdAt" :label="$t('createdAt')" width="180">
          <template slot-scope="scope">
            {{ $manba(scope.row.createdAt).format("ymdhms") }}
          </template>
        </el-table-column>

        <el-table-column :label="$t('actions')" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="!scope.row.isRead"
              size="mini"
              type="primary"
              @click.stop="markAsRead(scope.row)"
              plain
            >
              {{ $t("markRead") }}
            </el-button>
            <el-button
              v-if="!scope.row.isResolved"
              size="mini"
              type="success"
              @click.stop="resolveAlert(scope.row)"
            >
              {{ $t("resolve") }}
            </el-button>
            <el-tag v-else type="success" size="small">
              {{ $t("resolved") }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <div class="view-more" v-if="recentAlerts.length >= 10">
        <el-button type="text" @click="viewAllAlerts">
          {{ $t("viewAllAlerts") }} <i class="el-icon-arrow-right"></i>
        </el-button>
      </div>
    </el-card>

    <!-- Resolve Alert Dialog -->
    <el-dialog
      :title="$t('resolveAlert')"
      :visible.sync="resolveDialog"
      width="500px"
    >
      <el-form :model="resolveForm" ref="resolveForm">
        <el-form-item :label="$t('alertTitle')">
          <el-input v-model="currentAlert.title" disabled></el-input>
        </el-form-item>
        <el-form-item :label="$t('resolutionNote')" prop="resolutionNote">
          <el-input
            type="textarea"
            v-model="resolveForm.resolutionNote"
            :rows="4"
            :placeholder="$t('enterResolutionNote')"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="resolveDialog = false">{{ $t("cancel") }}</el-button>
        <el-button type="primary" @click="confirmResolve" :loading="resolving">
          {{ $t("confirm") }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import alert from "@/api/alert";

export default {
  name: "Alert",
  data() {
    return {
      statistics: {
        critical: 0,
        warning: 0,
        info: 0,
        totalUnresolved: 0,
        unread: 0,
        byType: {},
      },
      recentAlerts: [],
      loading: false,
      checkingAlerts: false,
      resolving: false,
      resolveDialog: false,
      currentAlert: {},
      resolveForm: {
        resolutionNote: "",
      },
    };
  },
  mounted() {
    this.loadData();
    // Auto-refresh every 5 minutes
    this.autoRefreshTimer = setInterval(() => {
      this.loadData(true);
    }, 5 * 60 * 1000);
  },
  beforeDestroy() {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
    }
  },
  methods: {
    loadData(silent = false) {
      let _this = this;
      if (!silent) _this.loading = true;

      // Load statistics
      alert.getStatistics({
        success: (res) => {
          if (res.code === 200) {
            _this.statistics = res.data || {
              critical: 0,
              warning: 0,
              info: 0,
              totalUnresolved: 0,
              unread: 0,
              byType: {},
            };
          }
          _this.loading = false;
        },
      });

      // Load recent alerts
      alert.getRecentAlerts({
        param: { limit: 10 },
        success: (res) => {
          if (res.code === 200) {
            _this.recentAlerts = res.data || [];
          }
        },
      });
    },

    refreshAlerts() {
      this.loadData();
    },

    checkAlertsNow() {
      let _this = this;
      _this.checkingAlerts = true;

      alert.checkNow({
        success: (res) => {
          _this.checkingAlerts = false;
          if (res.code === 200) {
            _this.$message.success(_this.$t("alertCheckCompleted"));
            _this.loadData();
          } else {
            _this.$message.error(res.msg || _this.$t("operationFailed"));
          }
        },
      });
    },

    markAsRead(alertItem) {
      let _this = this;

      alert.markAsRead({
        param: { id: alertItem.id },
        success: (res) => {
          if (res.code === 200) {
            _this.$message.success(_this.$t("markedAsRead"));
            alertItem.isRead = true;
            _this.loadData(true);
          } else {
            _this.$message.error(res.msg || _this.$t("operationFailed"));
          }
        },
      });
    },

    resolveAlert(alertItem) {
      this.currentAlert = alertItem;
      this.resolveForm.resolutionNote = "";
      this.resolveDialog = true;
    },

    confirmResolve() {
      let _this = this;
      _this.resolving = true;

      alert.resolveAlert({
        param: {
          id: _this.currentAlert.id,
          resolutionNote: _this.resolveForm.resolutionNote,
        },
        success: (res) => {
          _this.resolving = false;
          if (res.code === 200) {
            _this.$message.success(_this.$t("alertResolved"));
            _this.resolveDialog = false;
            _this.loadData();
          } else {
            _this.$message.error(res.msg || _this.$t("operationFailed"));
          }
        },
      });
    },

    handleRowClick(row) {
      if (!row.isRead) {
        this.markAsRead(row);
      }
    },

    getRowClassName({ row }) {
      if (!row.isRead) return "unread-row";
      if (row.isResolved) return "resolved-row";
      return "";
    },

    getSeverityType(severity) {
      const types = {
        CRITICAL: "danger",
        WARNING: "warning",
        INFO: "info",
      };
      return types[severity] || "info";
    },

    getSeverityName(severity) {
      const names = {
        CRITICAL: this.$t("critical"),
        WARNING: this.$t("warning"),
        INFO: this.$t("information"),
      };
      return names[severity] || severity;
    },

    getAlertTypeName(type) {
      const names = {
        LOW_BANK_BALANCE: this.$t("lowBankBalance"),
        LOW_AGENCY_BALANCE: this.$t("lowAgencyBalance"),
        LOW_CARD_BALANCE: this.$t("lowCardBalance"),
        LOW_CASH_BALANCE: this.$t("lowCashBalance"),
        PENDING_FUNDS_HIGH: this.$t("pendingFundsHigh"),
        UNRESOLVED_INCIDENT: this.$t("unresolvedIncident"),
        LARGE_TRANSACTION: this.$t("largeTransaction"),
        FAILED_TRANSACTION_RATE: this.$t("failedTransactionRate"),
        PENDING_CASH_LENDING: this.$t("pendingCashLending"),
      };
      return names[type] || type;
    },

    formatCurrency(value) {
      if (!value) return "0";
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "XOF",
        minimumFractionDigits: 0,
      }).format(value);
    },

    viewAllAlerts() {
      //this.$router.push({ path: "/admin/alerts" });
      this.$router.push({ name: "alerts" });
    },

    openSettings() {
      //this.$router.push({ path: "/admin/alert-settings" });
      this.$message.info(this.$t("comingSoon"));
    },
  },
};
</script>

<style lang="scss" scoped>
.alert-dashboard {
  padding: 20px;

  .stats-row {
    margin-bottom: 20px;
  }

  .stat-card {
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &.critical {
      border-left: 4px solid #f56c6c;
      .stat-content i {
        color: #f56c6c;
      }
    }

    &.warning {
      border-left: 4px solid #e6a23c;
      .stat-content i {
        color: #e6a23c;
      }
    }

    &.info {
      border-left: 4px solid #409eff;
      .stat-content i {
        color: #409eff;
      }
    }

    &.total {
      border-left: 4px solid #67c23a;
      .stat-content i {
        color: #67c23a;
      }
    }

    .stat-content {
      display: flex;
      align-items: center;
      padding: 10px 0;

      i {
        font-size: 48px;
        margin-right: 20px;
      }

      .stat-info {
        flex: 1;

        .stat-value {
          font-size: 32px;
          font-weight: bold;
          line-height: 1;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 14px;
          color: #909399;
        }
      }
    }
  }

  .chart-row {
    margin-bottom: 20px;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .alert-type-list {
    .alert-type-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid #ebeef5;

      &:last-child {
        border-bottom: none;
      }

      .type-name {
        font-size: 14px;
      }
    }

    .no-data {
      text-align: center;
      padding: 40px 0;
      color: #909399;
    }
  }

  .quick-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .el-button {
      width: 100%;
    }
  }

  .recent-alerts-card {
    margin-top: 20px;

    .alert-title {
      display: flex;
      align-items: center;
      gap: 8px;

      .unread-icon {
        color: #f56c6c;
        font-size: 16px;
      }
    }

    .alert-details {
      padding: 20px;
      background: #f5f7fa;
      border-radius: 4px;

      p {
        margin: 8px 0;
        line-height: 1.6;

        strong {
          color: #303133;
          margin-right: 8px;
        }
      }
    }

    .view-more {
      text-align: center;
      margin-top: 20px;
    }
  }

  ::v-deep .unread-row {
    background-color: #fef0f0;
    font-weight: 600;
  }

  ::v-deep .resolved-row {
    opacity: 0.6;
  }
}

@media (max-width: 768px) {
  .alert-dashboard {
    padding: 10px;

    .stat-card .stat-content {
      i {
        font-size: 32px;
        margin-right: 10px;
      }

      .stat-info .stat-value {
        font-size: 24px;
      }
    }
  }
}
</style>
